import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { initMercadoPago, CardPayment, Payment } from '@mercadopago/sdk-react';
import { CreditCard, Smartphone, FileText, Shield, CheckCircle, ArrowLeft } from 'lucide-react';
import { paymentsAPI, subscriptionsAPI } from '../api';

// PCI Compliance: dados do cartão são capturados apenas pelos Secure Fields do Payment Brick
// (SDK JS V2 / @mercadopago/sdk-react). O backend recebe somente o token, nunca número/CVV.
// Inicializar Mercado Pago com a Public Key
const MERCADOPAGO_PUBLIC_KEY = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY;

console.log('🔑 Public Key configurada:', MERCADOPAGO_PUBLIC_KEY ? 'SIM' : 'NÃO');
console.log('🔑 Public Key (primeiros 20 chars):', MERCADOPAGO_PUBLIC_KEY ? MERCADOPAGO_PUBLIC_KEY.substring(0, 20) : 'INDEFINIDA');

if (MERCADOPAGO_PUBLIC_KEY) {
  try {
    initMercadoPago(MERCADOPAGO_PUBLIC_KEY, {
      locale: 'pt-BR'
    });
    console.log('✅ Mercado Pago SDK inicializado com sucesso!');
  } catch (error) {
    console.error('❌ Erro ao inicializar Mercado Pago SDK:', error);
  }
} else {
  console.error('❌ VITE_MERCADOPAGO_PUBLIC_KEY não está configurado!');
  console.error('   Adicione no arquivo frontend/.env:');
  console.error('   VITE_MERCADOPAGO_PUBLIC_KEY=TEST-76089bf4-dcd7-4482-af41-95a24c2aacd9');
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan');
  const invoiceId = searchParams.get('invoice');

  const [selectedMethod, setSelectedMethod] = useState('pix');
  const [plan, setPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [autoRecurring, setAutoRecurring] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [boletoData, setBoletoData] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [cardCpf, setCardCpf] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerFirstName, setPayerFirstName] = useState('');
  const [payerLastName, setPayerLastName] = useState('');

  const paymentMethods = [
    {
      id: 'pix',
      name: 'PIX',
      icon: Smartphone,
      description: 'Aprovação instantânea',
      badge: 'Recomendado'
    },
    {
      id: 'credit_card',
      name: 'Cartão de Crédito',
      icon: CreditCard,
      description: 'Parcelamento disponível'
    },
    {
      id: 'debit_card',
      name: 'Cartão de Débito',
      icon: CreditCard,
      description: 'Débito direto na conta'
    },
    {
      id: 'boleto',
      name: 'Boleto',
      icon: FileText,
      description: 'Vence em 3 dias úteis'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Obter email do usuário do localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.email) {
        setUserEmail(userData.email);
        setPayerEmail(userData.email);
      }
      if (userData.name) {
        const parts = userData.name.trim().split(/\s+/);
        setPayerFirstName(parts[0] || '');
        setPayerLastName(parts.slice(1).join(' ') || '');
      }

      const [plansData, subData] = await Promise.all([
        subscriptionsAPI.getPlans(),
        subscriptionsAPI.getMySubscription()
      ]);

      setSubscription(subData);
      
      // Buscar plano baseado na assinatura, não no parâmetro
      if (subData && subData.plan) {
        const selectedPlan = plansData.find(p => p.name === subData.plan);
        setPlan(selectedPlan || { 
          id: subData.id, 
          name: subData.plan, 
          price: subData.price 
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePixPayment = async () => {
    if (!subscription) {
      alert('Erro: Assinatura não encontrada!');
      return;
    }

    setProcessing(true);
    try {
      const response = await paymentsAPI.createPix();
      setPixData(response.pix);
      setSuccessMessage('PIX gerado com sucesso! Escaneie o QR Code ou copie o código.');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
      alert('Erro ao gerar PIX: ' + errorMsg);
      console.error('Erro completo:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleBoletoPayment = async () => {
    if (!subscription) {
      alert('Erro: Assinatura não encontrada!');
      return;
    }

    setProcessing(true);
    try {
      const response = await paymentsAPI.createBoleto();
      setBoletoData(response.boleto);
      setSuccessMessage('Boleto gerado com sucesso!');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
      alert('Erro ao gerar Boleto: ' + errorMsg);
      console.error('Erro completo:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleCardPayment = async ({ paymentType, formData }) => {
    setProcessing(true);
    try {
      // O Payment Brick retorna { paymentType, formData }
      // formData contém: token, payment_method_id (bandeira específica), installments, etc.
      const token = formData?.token;
      // Usar payment_method_id específico do formData (visa, master, etc.) se disponível
      // Caso contrário, usar paymentType genérico como fallback
      const paymentMethodId = formData?.payment_method_id || 
                              (paymentType === 'credit_card' ? 'credit_card' : 'debit_card');
      const installments = formData?.installments || 1;
      const issuerId = formData?.issuer_id;

      console.log('💳 Dados do Payment Brick:', {
        paymentType,
        payment_method_id: formData?.payment_method_id,
        token: token ? '***' + token.slice(-4) : 'não encontrado',
        installments,
        issuerId
      });

      if (!token) {
        alert('Erro: Token do cartão não foi gerado. Tente novamente.');
        setProcessing(false);
        return;
      }

      const cpfDigits = cardCpf ? String(cardCpf).replace(/\D/g, '') : '';
      if (cpfDigits.length !== 11) {
        alert('Informe o CPF do titular do cartão (11 dígitos).');
        setProcessing(false);
        return;
      }
      const email = (payerEmail || '').trim();
      const firstName = (payerFirstName || '').trim();
      const lastName = (payerLastName || '').trim();
      if (!email || !firstName || !lastName) {
        alert('Preencha nome, sobrenome e e-mail do titular do cartão.');
        setProcessing(false);
        return;
      }

      const response = await paymentsAPI.createCardPayment({
        token: token,
        paymentMethodId: paymentMethodId,
        installments: installments,
        issuerId: issuerId,
        saveCard: saveCard,
        autoRecurring: autoRecurring,
        payerCpf: cpfDigits,
        payerEmail: email,
        payerFirstName: firstName,
        payerLastName: lastName
      });

      // O backend retorna status em response.payment.status ou response.card.status
      const paymentStatus = response.payment?.status || response.card?.status || response.status;

      console.log('📊 Resposta completa do backend:', response);
      console.log('📊 Status do pagamento:', paymentStatus);

      if (paymentStatus === 'approved') {
        setSuccessMessage('Pagamento aprovado com sucesso!');
        setTimeout(() => {
          navigate('/billing');
        }, 3000);
      } else if (paymentStatus === 'in_process' || paymentStatus === 'pending') {
        setSuccessMessage('Pagamento em análise. Você será notificado quando for aprovado.');
      } else {
        const statusDetail = response.card?.statusDetail || response.payment?.statusDetail || '';
        alert(`Pagamento recusado. Status: ${paymentStatus}${statusDetail ? ` - ${statusDetail}` : ''}. Tente outro cartão ou forma de pagamento.`);
      }
    } catch (error) {
      alert('Erro ao processar pagamento: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const onSubmit = async () => {
    if (selectedMethod === 'pix') {
      await handlePixPayment();
    } else if (selectedMethod === 'boleto') {
      await handleBoletoPayment();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (successMessage && (pixData || boletoData)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {successMessage}
              </h2>
            </div>

            {pixData && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                    Escaneie o QR Code com o app do seu banco
                  </p>
                  <div className="flex justify-center mb-4">
                    <img 
                      src={`data:image/png;base64,${pixData.qrCodeBase64}`} 
                      alt="QR Code PIX" 
                      className="w-64 h-64"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Ou copie o código PIX:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={pixData.copiaECola}
                      readOnly
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(pixData.copiaECola);
                        alert('Código copiado!');
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Copiar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {boletoData && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                        Código de Barras:
                      </label>
                      <input
                        type="text"
                        value={boletoData.barcode}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                        Linha Digitável:
                      </label>
                      <input
                        type="text"
                        value={boletoData.lineCode}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>

                <a
                  href={boletoData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-center font-medium"
                >
                  Ver Boleto Completo (PDF)
                </a>
              </div>
            )}

            <button
              onClick={() => navigate('/billing')}
              className="w-full mt-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Voltar para Pagamentos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/billing')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Pagamento da Fatura
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Escolha a forma de pagamento que preferir
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Métodos de Pagamento */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Selecione a forma de pagamento
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {paymentMethods.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelectedMethod(method.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedMethod === method.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <Icon className={`w-6 h-6 ${
                          selectedMethod === method.id ? 'text-blue-500' : 'text-gray-400'
                        }`} />
                        {method.badge && (
                          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full">
                            {method.badge}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {method.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {method.description}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Formulário de Cartão */}
              {(selectedMethod === 'credit_card' || selectedMethod === 'debit_card') && (
                <div className="space-y-4">
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dados do titular do cartão (conforme no cartão)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nome</label>
                        <input
                          type="text"
                          placeholder="Nome"
                          value={payerFirstName}
                          onChange={(e) => setPayerFirstName(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Sobrenome</label>
                        <input
                          type="text"
                          placeholder="Sobrenome"
                          value={payerLastName}
                          onChange={(e) => setPayerLastName(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">E-mail</label>
                      <input
                        type="email"
                        placeholder="email@exemplo.com"
                        value={payerEmail}
                        onChange={(e) => setPayerEmail(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">CPF do titular do cartão</label>
                      <input
                        type="text"
                        placeholder="000.000.000-00"
                        value={cardCpf}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                        if (digits.length <= 3) setCardCpf(digits);
                        else if (digits.length <= 6) setCardCpf(digits.slice(0, 3) + '.' + digits.slice(3));
                        else if (digits.length <= 9) setCardCpf(digits.slice(0, 3) + '.' + digits.slice(3, 6) + '.' + digits.slice(6));
                        else setCardCpf(digits.slice(0, 3) + '.' + digits.slice(3, 6) + '.' + digits.slice(6, 9) + '-' + digits.slice(9, 11));
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                      maxLength={14}
                    />
                    </div>
                    <Payment
                      initialization={{
                        amount: plan?.price || subscription?.price || 0,
                        payer: {
                          email: payerEmail || userEmail || '',
                          entityType: 'individual'
                        }
                      }}
                      onSubmit={handleCardPayment}
                      customization={{
                        visual: {
                          style: {
                            theme: 'dark'
                          }
                        },
                        paymentMethods: {
                          ...(selectedMethod === 'credit_card' && { creditCard: 'all' }),
                          ...(selectedMethod === 'debit_card' && { debitCard: 'all' })
                        }
                      }}
                    />
                  </div>

                  {/* Opções de Cartão */}
                  <div className="space-y-3 mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="w-5 h-5 text-blue-500 rounded"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Salvar este cartão para próximos pagamentos
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Seus dados ficam seguros e criptografados
                        </p>
                      </div>
                    </label>

                    {saveCard && (
                      <label className="flex items-center gap-3 cursor-pointer pl-8 border-l-2 border-blue-500">
                        <input
                          type="checkbox"
                          checked={autoRecurring}
                          onChange={(e) => setAutoRecurring(e.target.checked)}
                          className="w-5 h-5 text-blue-500 rounded"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Ativar cobrança automática mensal
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Será cobrado automaticamente no último dia do mês
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* Botão para PIX/Boleto */}
              {(selectedMethod === 'pix' || selectedMethod === 'boleto') && (
                <button
                  onClick={onSubmit}
                  disabled={processing}
                  className="w-full py-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Gerando...
                    </span>
                  ) : (
                    `Gerar ${selectedMethod === 'pix' ? 'PIX' : 'Boleto'}`
                  )}
                </button>
              )}
            </div>

            {/* Segurança */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                    Pagamento Seguro
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Seus dados estão protegidos com criptografia de ponta a ponta. 
                    Processamento via Mercado Pago, certificado PCI-DSS.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo do Pedido */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg sticky top-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Resumo
              </h2>

              {plan && (
                <div className="space-y-4">
                  <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Plano {plan.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Assinatura mensal
                    </p>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">Valor:</span>
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        R$ {plan.price.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {subscription && subscription.nextPaymentDate && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                      <div className="flex justify-between">
                        <span>Vencimento:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Date(subscription.nextPaymentDate).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span className="text-gray-900 dark:text-white">Total:</span>
                      <span className="text-blue-500">
                        R$ {plan.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

